/*

A wrapper component that smoothly transitions its height to fit its contents, whenever
those contents are updated. This is useful for sections that expand/collapse, variable-length
lists, etc.

Any props are passed through to the DOM element. Accepts a custom `tagName` as a prop,
which defaults to "div".

  <AnimateHeight className="my_container" tagName="section">
    {
      collapsed ? null : theContent
    }
  </AnimateHeight>

The animation is performed using a temporary set of CSS animation keyframes, from the old to the
new total measured height of its contents. When the animation completes, the animation is
removed and the element will use a `height` based on the value of the `heightAtRest` prop:
  - If `heightAtRest:'fixed'` (the default), it will retain the fixed pixel height of the
    final animation frame.
  - If `heightAtRest:'auto'`, it will get `height:auto` which will allow it to adjust to
    further changes of its inner content that don't happen due to a component update. In
    most cases this should behave just like 'fixed' but may be unpredictable in some cases
    such as within vertical flexbox.
*/

import React from 'react'
import ReactDOM from 'react-dom'
import cx from 'classnames'
import T from 'prop-types'

let _animId = 0

class AnimateHeight extends React.Component {
  static displayName = 'AnimateHeight'

  static propTypes = {
    tagName: T.string,
    heightAtRest: T.oneOf(['fixed', 'auto'])
  }

  static defaultProps = {
    tagName: 'div',
    heightAtRest: 'fixed'
  }

  constructor(props) {
    super(props)
    this.state = {
      animation: null, //current running animation
      baseHeight: 'auto' //outer element height when not animating
    }
    this._preHeight = null
  }

  componentDidMount() {
    this._afterUpdate()
  }

  UNSAFE_componentWillUpdate() {
    // Ensure we have a valid starting measurement prior to update
    if (this._preHeight === null) {
      this._preHeight = this._innerRef.offsetHeight
    }
  }

  componentDidUpdate() {
    this._afterUpdate()
  }

  _afterUpdate = () => {
    const fromHeight = this._preHeight
    const toHeight = this._innerRef.offsetHeight
    if (toHeight !== fromHeight) {
      this.setState({
        animation: {
          name: `pwAnimHeight${_animId++}`,
          from: fromHeight,
          to: toHeight
        },
        baseHeight: this.props.heightAtRest === 'fixed' ? toHeight + 'px' : 'auto'
      })
    }
    this._preHeight = null
  }

  _onAnimEnd = e => {
    // Remove animation after it finishes
    if (e.target === ReactDOM.findDOMNode(this)) {
      this.setState({ animation: null })
    }
  }

  _bindInnerRef = el => {
    this._innerRef = el
  }

  render() {
    const { props, state } = this
    const { animation } = state
    const Tag = props.tagName
    const divProps = Object.assign({}, props)
    delete divProps.tagName
    delete divProps.heightAtRest
    return <Tag
      {...divProps}
      className={cx('pw_animate_height', props.className)}
      style={Object.assign({
        animationName: animation ? animation.name : 'none',
        height: state.baseHeight //height used
      }, props.style)}
      onAnimationEnd={animation ? this._onAnimEnd : null}
    >
      {animation ? <style>{
`@keyframes ${animation.name} {
  0% {height: ${animation.from}px;}
  100% {height: ${animation.to}px;}
}`
      }</style> : null}

      <div className="pw_animate_height_inner" ref={this._bindInnerRef}>
        {props.children}
      </div>
    </Tag>
  }
}

export default AnimateHeight
